import { useState } from "react";
import { Building2, Package, Film, TrendingUp, Users, GraduationCap, FileText, Mic, Plane } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

interface Service {
  title: string;
  subtitle: string;
  description: string;
  icon: any;
  keywords: string[];
  category: string;
}

const services: Service[] = [
  {
    title: "Imagefilm / Unternehmensfilm",
    subtitle: "Ihre Geschichte, emotional erzählt",
    description: "Ein guter Imagefilm ist mehr als bewegte Bilder – er ist das Herz Ihrer Marke. Wir erzählen die Geschichte und Expertise Ihres Unternehmens auf emotionale und authentische Weise, die Vertrauen schafft und Kunden überzeugt.",
    icon: Building2,
    keywords: ["imagefilm", "unternehmensfilm", "corporate", "brand"],
    category: "Corporate"
  },
  {
    title: "Industriefilm",
    subtitle: "Innovation sichtbar machen",
    description: "Produktionsprozesse, modernste Maschinen und technologische Innovationen verdienen eine professionelle Inszenierung. Wir zeigen die Kraft Ihrer Industrie direkt aus dem Herzen Ihres Unternehmens – beeindruckend und verständlich.",
    icon: Building2,
    keywords: ["industrie", "produktion", "technologie", "innovation"],
    category: "Industrie"
  },
  {
    title: "Produktfilm",
    subtitle: "Produkte, die verkaufen",
    description: "Zeigen Sie Ihre Produkte in Aktion! Unsere Produktfilme präsentieren Features, Vorteile und Anwendungsfälle so überzeugend, dass Kunden nicht widerstehen können. Perfekt für E-Commerce, Messen und Social Media.",
    icon: Package,
    keywords: ["produkt", "product", "showcase", "demo"],
    category: "Marketing"
  },
  {
    title: "Werbefilm / Commercial",
    subtitle: "Werbung, die fesselt",
    description: "Hochwertige, cinematische Werbefilme, die im Gedächtnis bleiben. Ob für TV, Kino oder Online-Kampagnen – wir produzieren Commercials, die Emotionen wecken und Ihre Botschaft kraftvoll transportieren.",
    icon: Film,
    keywords: ["werbefilm", "commercial", "werbung", "kampagne"],
    category: "Marketing"
  },
  {
    title: "Promotionsfilm",
    subtitle: "Aufmerksamkeit, die zählt",
    description: "Aktionen, Angebote oder Produkteinführungen brauchen maximale Aufmerksamkeit. Unsere Promotionsfilme sind dynamisch, prägnant und sorgen dafür, dass Ihre Botschaft ankommt – genau zur richtigen Zeit.",
    icon: TrendingUp,
    keywords: ["promotion", "promo", "aktion", "angebot"],
    category: "Marketing"
  },
  {
    title: "Recruitingfilm / Employer Branding",
    subtitle: "Talente begeistern, nicht nur informieren",
    description: "Die besten Talente wollen wissen, wofür sie arbeiten. Unsere Recruitingfilme zeigen Ihre Unternehmenskultur authentisch und emotional – so sprechen Sie genau die Mitarbeiter:innen an, die zu Ihnen passen.",
    icon: Users,
    keywords: ["recruiting", "employer", "branding", "karriere", "team"],
    category: "Corporate"
  },
  {
    title: "Schulungs- & E-Learning-Videos",
    subtitle: "Wissen, das hängen bleibt",
    description: "Komplexe Prozesse, Sicherheitsthemen oder Produktschulungen – wir vermitteln Wissen verständlich und anschaulich. Ihre Mitarbeiter:innen und Kund:innen lernen effizienter mit professionellen Videos.",
    icon: GraduationCap,
    keywords: ["schulung", "elearning", "training", "education"],
    category: "Bildung"
  },
  {
    title: "Erklärfilm (ohne Animation)",
    subtitle: "Klarheit durch echte Bilder",
    description: "Komplexe Produkte oder Dienstleistungen einfach erklärt – mit realen Szenen, klarer Sprache und verständlichen Bildern. Ihre Kund:innen verstehen auf Anhieb, was Sie anbieten und warum es für sie relevant ist.",
    icon: FileText,
    keywords: ["erklärfilm", "explainer", "erklärvideo", "tutorial"],
    category: "Bildung"
  },
  {
    title: "Dokumentation / Reportage",
    subtitle: "Echte Geschichten, echt erzählt",
    description: "Menschen, Prozesse und Geschichten hinter Ihrem Unternehmen verdienen eine professionelle Dokumentation. Wir fangen authentische Momente ein und erzählen Ihre Story so, wie sie wirklich ist.",
    icon: Film,
    keywords: ["dokumentation", "reportage", "documentary", "story"],
    category: "Kreativ"
  },
  {
    title: "Eventfilm / Messefilm",
    subtitle: "Events, die nachklingen",
    description: "Die Energie, Highlights und Emotionen Ihrer Veranstaltung festgehalten – professionell und atmosphärisch. Perfekt für Nachberichterstattung, Social Media oder als Teaser für kommende Events.",
    icon: Film,
    keywords: ["event", "messe", "veranstaltung", "kongress"],
    category: "Event"
  },
  {
    title: "Interview- & Testimonialfilm",
    subtitle: "Glaubwürdigkeit durch echte Stimmen",
    description: "Nichts überzeugt mehr als authentische Stimmen. Ob Mitarbeiter:innen, Kund:innen oder Führungskräfte – wir fangen echte Emotionen und ehrliche Meinungen ein, die Vertrauen und Nähe schaffen.",
    icon: Mic,
    keywords: ["interview", "testimonial", "statement", "kunde"],
    category: "Corporate"
  },
  {
    title: "Drohnenaufnahmen",
    subtitle: "Perspektiven, die beeindrucken",
    description: "Atemberaubende Luftaufnahmen für Außenansichten, Architektur, Unternehmensgelände und Events. Unsere Drohnenaufnahmen setzen Ihr Unternehmen perfekt in Szene – aus einer Perspektive, die begeistert. Wir fliegen mit EU-Kompetenznachweis (A1/A3) und Drohnen-Haftpflichtversicherung.",
    icon: Plane,
    keywords: ["drohne", "drone", "aerial", "luftaufnahme"],
    category: "Speziell"
  }
];

const categories = ["Alle", "Corporate", "Marketing", "Industrie", "Bildung", "Event", "Kreativ", "Speziell"];

export const VideographyServicesGrid = () => {
  const [selectedCategory, setSelectedCategory] = useState("Alle");
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const filteredServices = selectedCategory === "Alle" 
    ? services 
    : services.filter(s => s.category === selectedCategory);

  return (
    <>
      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 justify-center mb-12">
        {categories.map((category) => (
          <Badge
            key={category}
            variant={selectedCategory === category ? "default" : "outline"}
            className="cursor-pointer px-4 py-2 text-sm hover:scale-105 transition-all"
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </Badge>
        ))}
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredServices.map((service, index) => {
          const ServiceIcon = service.icon;
          
          return (
            <Card
              key={index}
              className="group relative overflow-hidden cursor-pointer border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/20 hover:-translate-y-2"
              onClick={() => setSelectedService(service)}
            >
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              {/* Content */}
              <div className="relative p-6 space-y-4">
                {/* Icon & Category Badge */}
                <div className="flex items-start justify-between">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                    <ServiceIcon className="w-7 h-7 text-primary" />
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {service.category}
                  </Badge>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold leading-tight group-hover:text-primary transition-colors">
                  {service.title}
                </h3>

                {/* Subtitle */}
                <p className="text-sm text-primary/80 font-medium">
                  {service.subtitle}
                </p>

                {/* Description Preview */}
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {service.description}
                </p>

                {/* Hover indicator */}
                <div className="flex items-center gap-2 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>Mehr erfahren</span>
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Service Details Dialog */}
      <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="max-w-2xl">
          {selectedService && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                    <selectedService.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="text-2xl mb-1">
                      {selectedService.title}
                    </DialogTitle>
                    <Badge variant="secondary">{selectedService.category}</Badge>
                  </div>
                </div>
              </DialogHeader>
              
              <DialogDescription className="space-y-4">
                <p className="text-lg font-semibold text-primary">
                  {selectedService.subtitle}
                </p>
                <p className="text-base text-foreground leading-relaxed">
                  {selectedService.description}
                </p>
                <div className="flex flex-wrap gap-2 pt-4">
                  {selectedService.keywords.map((keyword, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </DialogDescription>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
